import React from 'react';
import { ArrowLeft, Globe, Lock, Pencil } from 'lucide-react';
import { CHANNEL, CHANNEL_LABELS } from '../../../utils/bookingSource';

const BookingHeader = ({
  onBack,
  backLabel = 'Back to bookings',
  status,
  statusColor,
  statusText,
  onUpdateStatus,
  bookingReference,
  createdAt,
  formatDate,
  channel = CHANNEL.INTERNAL,
  clientLabel = null,
}) => {
  const isCompleted = status === 'completed';
  const isCancelled = status === 'cancelled';
  const isExternal = channel === CHANNEL.EXTERNAL;
  const ChannelIcon = isExternal ? Lock : Globe;
  const channelLabel = CHANNEL_LABELS[channel] || CHANNEL_LABELS.internal;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors w-fit"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {backLabel}
        </button>
        <div className="flex items-center gap-2 sm:gap-3">
          <span
            className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold capitalize ${statusColor}`}
          >
            {statusText}
          </span>
          {!isCompleted && !isCancelled && (
            <button
              type="button"
              onClick={onUpdateStatus}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-surface text-gray-800 hover:bg-surface-muted transition-colors text-xs sm:text-sm font-medium"
            >
              <Pencil className="w-3.5 h-3.5" />
              Update status
            </button>
          )}
        </div>
      </div>

      {(bookingReference || createdAt) && (
        <div
          className={`rounded-xl border px-5 py-4 sm:px-6 sm:py-5 shadow-sm ${
            isExternal
              ? 'border-teal-300 border-l-4 border-l-teal-600 bg-teal-50/80'
              : 'border-border border-l-4 border-l-ink bg-surface'
          }`}
        >
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                isExternal
                  ? 'bg-teal-700 text-white'
                  : 'bg-ink text-white'
              }`}
            >
              <ChannelIcon className="h-3 w-3 shrink-0" aria-hidden />
              {channelLabel}
            </span>
            <span
              className={`text-[11px] font-medium ${
                isExternal ? 'text-teal-900/80' : 'text-ink-tertiary'
              }`}
            >
              {isExternal
                ? 'Your own client · off-platform'
                : 'VendaList marketplace'}
            </span>
          </div>
          <p
            className={`text-[11px] font-semibold uppercase tracking-[0.14em] mb-1 ${
              isExternal ? 'text-teal-800/70' : 'text-gray-500'
            }`}
          >
            {isExternal ? 'External booking' : 'Internal booking'}
          </p>
          <h1
            className={`text-xl sm:text-2xl font-bold tracking-tight ${
              isExternal ? 'text-teal-950' : 'text-gray-900'
            }`}
          >
            #{bookingReference}
          </h1>
          <div
            className={`mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-sm ${
              isExternal ? 'text-teal-900/75' : 'text-gray-500'
            }`}
          >
            {createdAt && formatDate ? (
              <span>Created {formatDate(createdAt)}</span>
            ) : null}
            {clientLabel ? <span>· {clientLabel}</span> : null}
          </div>
          {isExternal ? (
            <p className="mt-3 text-xs leading-relaxed text-teal-900/80 max-w-2xl">
              Private job outside marketplace payouts. Use invoices, contracts, and offline
              payment tools from this page.
            </p>
          ) : (
            <p className="mt-3 text-xs leading-relaxed text-ink-secondary max-w-2xl">
              Marketplace order — payments and earnings flow through platform accounting and
              payouts.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default BookingHeader;
